import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, MessageCircle, Clock, Shirt, Copy } from "lucide-react";
import { TiltCard } from "./TiltCard";
import { useLang } from "../store/lang";
import { toast } from "sonner";
import { whatsappUrl, VENUE_MAP_URL, VENUE_NAME, VENUE_ADDRESS, CEREMONY_DATE_LABEL } from "../config";

const PHOTOS = [
  { src: "/venue/lawn-night.jpeg", alt: "Aahil Villa Banquets lawn glowing at night" },
  { src: "/venue/hall-gold.jpeg", alt: "The grand banquet hall of Aahil Villa Banquets" },
  { src: "/venue/lawn-garden.jpeg", alt: "Outdoor garden space at Aahil Villa Banquets" },
  { src: "/venue/hall-stage.jpeg", alt: "Decorated stage inside the banquet hall" },
];

export const Invitation = () => {
  const [photo, setPhoto] = useState(0);
  const { ur } = useLang();
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(`${VENUE_NAME} — ${VENUE_MAP_URL}`);
      toast.success("Venue details copied");
    } catch {
      toast.error("Could not copy — please long-press the address");
    }
  };

  return (
    <section id="invitation" className="relative py-28 sm:py-36 px-6" data-testid="invitation-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="eyebrow mb-4"
          >
            {ur ? "آپ کو پورے دل سے دعوت ہے" : "You are cordially invited"}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className={`font-display text-3xl sm:text-5xl text-amber-100 font-light ${ur ? "font-urdu" : ""}`}
            dir={ur ? "rtl" : "ltr"}
          >
            {ur ? "شاہی دعوت نامہ" : "The Royal Invitation"}
          </motion.h2>
        </div>

        <TiltCard
          max={3}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="glass-card rounded-[2rem] overflow-hidden grid lg:grid-cols-2"
          data-testid="invitation-card"
        >
          <div className="relative min-h-[320px] lg:min-h-[520px]">
            <img
              data-testid="venue-main-photo"
              src={PHOTOS[photo].src}
              alt={PHOTOS[photo].alt}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d1b] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 glass-card rounded-full px-5 py-2 text-xs tracking-[0.25em] uppercase text-amber-100">
              {VENUE_NAME}
            </div>
            <div className="absolute bottom-6 right-6 flex gap-2">
              {PHOTOS.map((p, i) => (
                <button
                  key={p.src}
                  data-testid={`venue-thumb-${i}`}
                  onClick={() => setPhoto(i)}
                  aria-label={`View venue photo ${i + 1}`}
                  className={`w-12 h-12 rounded-lg overflow-hidden border transition-[border-color,transform,opacity,box-shadow] duration-300 ${
                    photo === i
                      ? "border-amber-300 scale-110 shadow-[0_0_14px_rgba(212,175,55,0.5)]"
                      : "border-white/20 opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={p.src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <p className={`text-3xl text-rose-200/90 ${ur ? "font-urdu" : "font-script"}`} dir={ur ? "rtl" : "ltr"}>
              {ur ? "دل کی خوشیوں کے ساتھ" : "with joy in our hearts"}
            </p>
            <h3 className={`font-royal text-2xl sm:text-3xl gold-text mt-4 leading-snug ${ur ? "font-urdu" : ""}`} dir={ur ? "rtl" : "ltr"}>
              {ur ? "ہایا نور جمال کی تقریبِ عقیقہ" : "The Aqiqah Ceremony of Haya Noor Jamal"}
            </h3>

            <ul className="mt-8 space-y-5 text-slate-300 font-light">
              <li className="flex items-start gap-4">
                <Clock size={17} className="text-amber-300 mt-1 shrink-0" />
                <span dir={ur ? "rtl" : "ltr"}>
                  <span className="text-amber-100">{CEREMONY_DATE_LABEL}</span>
                  <span className="block text-sm text-slate-400 mt-0.5">
                    {ur ? "شام 7 بجے سے شاندار استقبالیہ" : "Grand reception from 7:00 PM onwards"}
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-4">
                <MapPin size={17} className="text-amber-300 mt-1 shrink-0" />
                <span dir={ur ? "rtl" : "ltr"}>
                  <span className="text-amber-100">{VENUE_NAME}</span>
                  <span className="block text-sm text-slate-400 mt-0.5">{VENUE_ADDRESS}</span>
                  <button
                    data-testid="copy-address-btn"
                    onClick={copyAddress}
                    className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-amber-200 transition-colors duration-300 mt-0.5"
                  >
                    <Copy size={12} /> {ur ? "لنک کاپی کریں" : "tap to copy venue link"}
                  </button>
                </span>
              </li>
              <li className="flex items-start gap-4">
                <Shirt size={17} className="text-amber-300 mt-1 shrink-0" />
                <span dir={ur ? "rtl" : "ltr"}>
                  <span className="text-amber-100">{ur ? "پہناوہ" : "Dress code"}</span>
                  <span className="block text-sm text-slate-400 mt-0.5">
                    {ur ? "ستاروں سا چمکتا شاہی لباس" : "Royal stardust & fairytale formal"}
                  </span>
                </span>
              </li>
            </ul>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                data-testid="whatsapp-rsvp-btn"
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
              >
                <MessageCircle size={16} /> {ur ? "واٹس ایپ پر RSVP" : "RSVP on WhatsApp"}
              </a>
              <a
                data-testid="directions-btn"
                href={VENUE_MAP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
                dir={ur ? "rtl" : "ltr"}
              >
                <MapPin size={15} /> {ur ? "راستہ دیکھیں" : "Get Directions"}
              </a>
            </div>
          </div>
        </TiltCard>
      </div>
    </section>
  );
};
